/**
 * CreateDocumentModal Component - Create new document with type selection
 */

import React, { useState, useRef, useCallback } from 'react'
import { XMarkIcon, DocumentTextIcon, CodeBracketIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'

import type { DocumentType } from '../../types'
import { latexAPI } from '../../api/latex'
import { useNavigate } from 'react-router-dom'
import { logError, logDebug } from '@utils/logger'

interface CreateDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (title: string, documentType: DocumentType, content?: string, skipTemplate?: boolean) => Promise<string> // Returns document ID
}

const documentTypes: Array<{ type: DocumentType; label: string; icon: React.ReactNode; description: string }> = [
  {
    type: 'text',
    label: 'Plain Text',
    icon: <DocumentTextIcon className="w-6 h-6" />,
    description: 'Simple text document'
  },
  {
    type: 'markdown',
    label: 'Markdown',
    icon: <CodeBracketIcon className="w-6 h-6" />,
    description: 'Markdown formatted document'
  },
  {
    type: 'latex',
    label: 'LaTeX',
    icon: <DocumentTextIcon className="w-6 h-6" />,
    description: 'LaTeX document with project structure'
  },
  {
    type: 'docx',
    label: 'Word Document',
    icon: <DocumentTextIcon className="w-6 h-6" />,
    description: 'Microsoft Word document'
  }
]

const latexTemplates = [
  {
    name: 'Guided Template',
    description: 'Complete research paper template with educational comments, bibliography, and sample images',
    content: ''  // Content will be loaded from template on backend
  },
  {
    name: 'Article',
    description: 'Basic article template',
    content: `\\documentclass{article}
\\usepackage[utf8]{inputenc}
\\usepackage{amsmath}
\\usepackage{amsfonts}
\\usepackage{amssymb}
\\usepackage{graphicx}

\\title{Your Title Here}
\\author{Your Name}
\\date{\\today}

\\begin{document}

\\maketitle

\\section{Introduction}

Your content here.

\\end{document}`
  },
  {
    name: 'Report',
    description: 'Report template with chapters',
    content: `\\documentclass{report}
\\usepackage[utf8]{inputenc}
\\usepackage{amsmath}
\\usepackage{amsfonts}
\\usepackage{amssymb}
\\usepackage{graphicx}

\\title{Your Report Title}
\\author{Your Name}
\\date{\\today}

\\begin{document}

\\maketitle
\\tableofcontents

\\chapter{Introduction}

Your content here.

\\end{document}`
  },
  {
    name: 'Beamer',
    description: 'Presentation template',
    content: `\\documentclass{beamer}
\\usepackage[utf8]{inputenc}
\\usepackage{amsmath}
\\usepackage{amsfonts}
\\usepackage{amssymb}

\\title{Your Presentation Title}
\\author{Your Name}
\\date{\\today}

\\begin{document}

\\frame{\\titlepage}

\\begin{frame}
\\frametitle{Introduction}

Your content here.

\\end{frame}

\\end{document}`
  },
  {
    name: 'Letter',
    description: 'Letter template',
    content: `\\documentclass{letter}
\\usepackage[utf8]{inputenc}

\\address{Your Address}
\\date{\\today}

\\begin{document}

\\begin{letter}{Recipient Address}

\\opening{Dear Sir/Madam,}

Your letter content here.

\\closing{Sincerely,}

\\end{letter}

\\end{document}`
  }
]

export const CreateDocumentModal: React.FC<CreateDocumentModalProps> = ({
  isOpen,
  onClose,
  onCreate
}) => {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [selectedType, setSelectedType] = useState<DocumentType>('text')
  const [selectedTemplate, setSelectedTemplate] = useState<string>('Guided Template')
  const [isCreating, setIsCreating] = useState(false)
  const [showZipUpload, setShowZipUpload] = useState(false)
  const [selectedZipFile, setSelectedZipFile] = useState<File | null>(null)
  const [mainFileName, setMainFileName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [fileSizeError, setFileSizeError] = useState<string | null>(null)
  const zipFileInputRef = useRef<HTMLInputElement>(null)

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error('Please enter a document title')
      return
    }

    // If LaTeX and ZIP upload is selected, require ZIP file
    if (selectedType === 'latex' && showZipUpload && !selectedZipFile) {
      toast.error('Please select a ZIP file to upload')
      return
    }

    // CLIENT-SIDE VALIDATION: Check file size BEFORE creating document
    if (selectedType === 'latex' && showZipUpload && selectedZipFile) {
      const MAX_UPLOAD_SIZE = 50 * 1024 * 1024 // 50MB
      if (selectedZipFile.size > MAX_UPLOAD_SIZE) {
        const fileSizeMB = (selectedZipFile.size / (1024 * 1024)).toFixed(2)
        const errorMessage = `File is too large (${fileSizeMB}MB). Maximum upload size is 50MB. Please reduce the file size and try again.`
        
        // Set error state to show in UI
        setFileSizeError(errorMessage)
        
        // Show error message - use a longer duration and ensure it's visible
        toast.error(errorMessage, { 
          duration: 12000, // 12 seconds to ensure user sees it
          position: 'top-center',
          style: {
            background: '#ef4444',
            color: '#fff',
            fontSize: '14px',
            padding: '16px',
            maxWidth: '500px'
          }
        })
        
        logError('[CreateDocumentModal] File too large rejected:', {
          fileName: selectedZipFile.name,
          fileSize: selectedZipFile.size,
          fileSizeMB: fileSizeMB,
          maxSize: MAX_UPLOAD_SIZE
        })
        
        // Reset form but keep modal open so user can select a different file
        setSelectedZipFile(null)
        setMainFileName('')
        if (zipFileInputRef.current) {
          zipFileInputRef.current.value = ''
        }
        return // Exit early - don't create document
      } else {
        // Clear error if file is valid
        setFileSizeError(null)
      }
    }

    setIsCreating(true)
    try {
      let content = ''
      
      // Get template content if LaTeX and template selected (not ZIP upload)
      // Note: "Guided Template" uses empty content - backend will copy template from disk
      if (selectedType === 'latex' && !showZipUpload && selectedTemplate !== 'Guided Template') {
        const template = latexTemplates.find(t => t.name === selectedTemplate)
        content = template?.content || ''
      } else if (selectedType === 'latex' && !showZipUpload && selectedTemplate === 'Guided Template') {
        // Guided template - use empty content, backend will copy template from disk
        content = ''
      } else if (selectedType === 'markdown') {
        content = '# ' + title + '\n\n'
      }

      // Create document
      // Skip template creation if ZIP upload is selected
      const skipTemplate: boolean = !!(selectedType === 'latex' && showZipUpload && selectedZipFile)
      const documentId = await onCreate(title.trim(), selectedType, content, skipTemplate)
      
      // If LaTeX and ZIP upload is selected, upload the ZIP file immediately
      let zipImportCompleted = false
      let uploadFailedWithSizeError = false // Flag to track if upload failed due to size
      
      if (selectedType === 'latex' && showZipUpload && selectedZipFile) {
        // CLIENT-SIDE VALIDATION: Check file size before attempting upload
        const MAX_UPLOAD_SIZE = 50 * 1024 * 1024 // 50MB
        if (selectedZipFile.size > MAX_UPLOAD_SIZE) {
          const fileSizeMB = (selectedZipFile.size / (1024 * 1024)).toFixed(2)
          const errorMessage = `File is too large (${fileSizeMB}MB). Maximum upload size is 50MB. Please reduce the file size and try again.`
          
          toast.error(errorMessage, { duration: 10000 })
          
          // Reset form
          setTitle('')
          setSelectedType('text')
          setSelectedTemplate('Guided Template')
          setShowZipUpload(false)
          setSelectedZipFile(null)
          setMainFileName('')
          if (zipFileInputRef.current) {
            zipFileInputRef.current.value = ''
          }
          
          setIsCreating(false)
          onClose()
          return // Exit early - don't create document or navigate
        }
        
        // Set uploading state BEFORE the async call to ensure UI updates
        setIsUploading(true)
        setUploadProgress(1) // Start at 1% so progress bar is visible
        logDebug('[CreateDocumentModal] Setting isUploading to true, progress to 1%')
        
        // Force a re-render to show the progress bar
        await new Promise(resolve => setTimeout(resolve, 50))
        
        // Track if upload was cancelled/errored to stop progress updates
        let uploadCancelled = false
        let uploadTimeout: NodeJS.Timeout | null = null
        
        try {
          logDebug('[CreateDocumentModal] Starting ZIP import...', {
            documentId,
            fileName: selectedZipFile.name,
            fileSize: selectedZipFile.size,
            mainFileName: mainFileName || undefined
          })
          
          // Set a timeout to detect if upload gets stuck (e.g., 413 error that doesn't propagate)
          uploadTimeout = setTimeout(() => {
            if (!uploadCancelled && !zipImportCompleted) {
              logError('[CreateDocumentModal] Upload timeout - checking if it failed silently')
              // Don't cancel here - let the actual error handling take care of it
            }
          }, 30000) // 30 second timeout
          
          const result = await latexAPI.importProject(
            documentId,
            selectedZipFile,
            mainFileName || undefined,
            (progress) => {
              // Don't update progress if upload was cancelled/errored
              if (uploadCancelled) {
                return
              }
              logDebug('[CreateDocumentModal] Upload progress callback:', progress)
              setUploadProgress(Math.max(1, progress)) // Keep at least 1% visible
            }
          )
          
          // Clear timeout on success
          if (uploadTimeout) {
            clearTimeout(uploadTimeout)
            uploadTimeout = null
          }
          logDebug('[CreateDocumentModal] ZIP import successful:', result)
          zipImportCompleted = true
          setUploadProgress(100)
          
          // Wait longer for backend to fully process and finalize ZIP extraction
          // This ensures files are ready before editor opens
          logDebug('[CreateDocumentModal] Waiting for backend to finalize ZIP processing...')
          setUploadProgress(95) // Show we're almost done
          await new Promise(resolve => setTimeout(resolve, 2000)) // Wait 2 seconds for processing
          logDebug('[CreateDocumentModal] Backend processing complete, navigating to editor')
        } catch (error: any) {
          // Stop progress updates immediately
          uploadCancelled = true
          
          // Clear timeout
          if (uploadTimeout) {
            clearTimeout(uploadTimeout)
            uploadTimeout = null
          }
          
          // Stop progress immediately
          setUploadProgress(0)
          setIsUploading(false)
          
          logError('[CreateDocumentModal] ZIP import failed:', {
            error,
            status: error.response?.status,
            statusCode: error.response?.statusCode,
            data: error.response?.data,
            message: error.message,
            code: error.code,
            fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
          })
          
          // Check if it's a file size error (413)
          const isFileTooLarge = error.response?.status === 413 || 
                                 error.response?.statusCode === 413 ||
                                 error.code === 'ECONNABORTED' && error.message?.includes('413') ||
                                 error.response?.data?.detail?.toLowerCase().includes('too large') ||
                                 error.response?.data?.detail?.toLowerCase().includes('exceeds') ||
                                 error.response?.data?.message?.toLowerCase().includes('too large') ||
                                 error.message?.toLowerCase().includes('too large') ||
                                 error.message?.toLowerCase().includes('413') ||
                                 error.message?.toLowerCase().includes('request entity too large')
          
          if (isFileTooLarge) {
            // File is too large - show clear error and DON'T navigate
            const errorMessage = error.response?.data?.detail || 
              error.response?.data?.message ||
              error.message ||
              'File is too large. Maximum upload size is 50MB. Please reduce the file size and try again.'
            
            logError('[CreateDocumentModal] File too large error from backend:', {
              status: error.response?.status,
              statusCode: error.response?.statusCode,
              detail: error.response?.data?.detail,
              message: error.message,
              code: error.code
            })
            
            // Show error message with enhanced visibility
            toast.error(errorMessage, { 
              duration: 12000, // 12 seconds
              position: 'top-center',
              style: {
                background: '#ef4444',
                color: '#fff',
                fontSize: '14px',
                padding: '16px',
                maxWidth: '500px'
              }
            })
            
            // Reset upload state
            setUploadProgress(0)
            setIsUploading(false)
            setIsCreating(false) // Allow modal to close
            
            // Reset form
            setTitle('')
            setSelectedType('text')
            setSelectedTemplate('Guided Template')
            setShowZipUpload(false)
            setSelectedZipFile(null)
            setMainFileName('')
            if (zipFileInputRef.current) {
              zipFileInputRef.current.value = ''
            }
            
            // Close modal and DON'T navigate
            onClose()
            uploadFailedWithSizeError = true // Mark that we should not navigate
            return // Exit early - don't continue to navigation code
          } else {
            // Other errors - show error but still navigate to editor so user can manually upload
            toast.error(
              error.response?.data?.detail || 
              error.response?.data?.message ||
              'ZIP upload failed. You can manually upload files in the editor.',
              { duration: 5000 }
            )
            // Still set progress to 100% so UI doesn't look stuck
            setUploadProgress(100)
            zipImportCompleted = false // Mark as not completed so we don't show import success
          }
        } finally {
          setIsUploading(false)
          if (zipImportCompleted) {
            setIsCreating(false) // Only close modal if upload succeeded
          }
        }
      }
      
      // Don't navigate if upload failed due to size error
      if (uploadFailedWithSizeError) {
        return // Exit early - modal already closed, don't navigate
      }
      
      // Navigate to editor after upload completes (or if no ZIP upload was attempted)
      if (zipImportCompleted || !(selectedType === 'latex' && showZipUpload && selectedZipFile)) {
        // Navigate to editor after upload completes
        // Reset form
        setTitle('')
        setSelectedType('text')
        setSelectedTemplate('Guided Template')
        setShowZipUpload(false)
        setSelectedZipFile(null)
        setMainFileName('')
        setUploadProgress(0)
        if (zipFileInputRef.current) {
          zipFileInputRef.current.value = ''
        }
        onClose()
        
        // Navigate to editor with query parameter if ZIP was imported
        const navPath = zipImportCompleted 
          ? `/editor/${documentId}?imported=true`
          : `/editor/${documentId}`
        navigate(navPath)
      } else {
        // No ZIP upload - navigate immediately after document creation
        // Reset form
        setTitle('')
        setSelectedType('text')
        setSelectedTemplate('Guided Template')
        setShowZipUpload(false)
        setSelectedZipFile(null)
        setMainFileName('')
        if (zipFileInputRef.current) {
          zipFileInputRef.current.value = ''
        }
        onClose()
        
        // Navigate to editor
        navigate(`/editor/${documentId}`)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create document')
      setIsUploading(false)
      setUploadProgress(0)
    } finally {
      // Only set isCreating to false if we're not uploading
      // This keeps the modal open during upload
      if (!isUploading) {
        setIsCreating(false)
      }
    }
  }

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    const zipFile = files.find(f => f.name.endsWith('.zip'))
    
    if (zipFile) {
      // Check file size immediately when dropped
      const MAX_UPLOAD_SIZE = 50 * 1024 * 1024 // 50MB
      if (zipFile.size > MAX_UPLOAD_SIZE) {
        const fileSizeMB = (zipFile.size / (1024 * 1024)).toFixed(2)
        const errorMessage = `File is too large (${fileSizeMB}MB). Maximum upload size is 50MB. Please reduce the file size and try again.`
        
        // Set the file so we can show it with the error message
        setSelectedZipFile(zipFile)
        setFileSizeError(errorMessage)
        
        toast.error(errorMessage, { 
          duration: 12000,
          position: 'top-center',
          style: {
            background: '#ef4444',
            color: '#fff',
            fontSize: '14px',
            padding: '16px',
            maxWidth: '500px'
          }
        })
      } else {
        setSelectedZipFile(zipFile)
        setFileSizeError(null) // Clear any previous error
      }
    } else {
      toast.error('Please drop a ZIP file')
    }
  }, [])

  const handleZipFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.name.endsWith('.zip')) {
        // Check file size immediately when selected
        const MAX_UPLOAD_SIZE = 50 * 1024 * 1024 // 50MB
        if (file.size > MAX_UPLOAD_SIZE) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)
          const errorMessage = `File is too large (${fileSizeMB}MB). Maximum upload size is 50MB. Please reduce the file size and try again.`
          
          // Set the file so we can show it with the error message
          setSelectedZipFile(file)
          setFileSizeError(errorMessage)
          
          toast.error(errorMessage, { 
            duration: 12000,
            position: 'top-center',
            style: {
              background: '#ef4444',
              color: '#fff',
              fontSize: '14px',
              padding: '16px',
              maxWidth: '500px'
            }
          })
        } else {
          setSelectedZipFile(file)
          setFileSizeError(null) // Clear any previous error
        }
      } else {
        toast.error('Please select a ZIP file')
      }
    }
  }, [])

  const handleClose = () => {
    // Don't allow closing during upload
    if (!isCreating && !isUploading) {
      setTitle('')
      setSelectedType('text')
      setSelectedTemplate('Blank')
      setShowZipUpload(false)
      setSelectedZipFile(null)
      setMainFileName('')
      setUploadProgress(0)
      setFileSizeError(null) // Clear error when closing
      if (zipFileInputRef.current) {
        zipFileInputRef.current.value = ''
      }
      onClose()
    }
  }

  if (!isOpen) return null

  // Debug: Log state when component renders
  if (isUploading || uploadProgress > 0) {
    logDebug('[CreateDocumentModal] Render with upload state:', { isUploading, uploadProgress, isOpen })
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={isUploading ? undefined : handleClose}
          className="absolute inset-0 bg-black/50"
          style={{ pointerEvents: isUploading ? 'none' : 'auto' }}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Create New Document
            </h2>
            <button
              onClick={handleClose}
              disabled={isCreating}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Title Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Document Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter document title"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isCreating}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isCreating) {
                    handleCreate()
                  }
                }}
              />
            </div>

            {/* Document Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Document Type *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {documentTypes.map((docType) => (
                  <button
                    key={docType.type}
                    onClick={() => setSelectedType(docType.type)}
                    disabled={isCreating}
                    className={`
                      flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all
                      ${selectedType === docType.type
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                      disabled:opacity-50
                    `}
                  >
                    <div className={`${selectedType === docType.type ? 'text-blue-600' : 'text-gray-600 dark:text-gray-400'}`}>
                      {docType.icon}
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {docType.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {docType.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* LaTeX Template Selection */}
            {selectedType === 'latex' && (
              <div className="space-y-4">
                {/* Choose between template or ZIP upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Start From
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      onClick={() => {
                        setShowZipUpload(false)
                        setSelectedTemplate('Guided Template')
                      }}
                      disabled={isCreating}
                      className={`
                        flex items-center gap-2 p-3 rounded-lg border-2 transition-all
                        ${!showZipUpload
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }
                        disabled:opacity-50
                      `}
                    >
                      <DocumentTextIcon className="w-5 h-5" />
                      <span className="text-sm font-medium">Template</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowZipUpload(true)
                        setSelectedTemplate('Guided Template')
                      }}
                      disabled={isCreating}
                      className={`
                        flex items-center gap-2 p-3 rounded-lg border-2 transition-all
                        ${showZipUpload
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }
                        disabled:opacity-50
                      `}
                    >
                      <ArrowUpTrayIcon className="w-5 h-5" />
                      <span className="text-sm font-medium">Upload ZIP</span>
                    </button>
                  </div>
                </div>

                {/* Show template selection if not ZIP upload */}
                {!showZipUpload && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Choose Template
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {latexTemplates.map((template) => (
                        <button
                          key={template.name}
                          onClick={() => setSelectedTemplate(template.name)}
                          disabled={isCreating}
                          className={`
                            text-left p-3 rounded-lg border-2 transition-all
                            ${selectedTemplate === template.name
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }
                            disabled:opacity-50
                          `}
                        >
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {template.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {template.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show ZIP upload interface if selected */}
                {showZipUpload && (
                  <div className="space-y-4">
                    {/* Drop Zone */}
                    <div
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`
                        border-2 border-dashed rounded-lg p-6 text-center transition-colors
                        ${isDragging
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : fileSizeError
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                            : selectedZipFile
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }
                      `}
                    >
                      {selectedZipFile ? (
                        <div className="space-y-3">
                          <DocumentTextIcon className={`w-10 h-10 mx-auto ${fileSizeError ? 'text-red-500' : 'text-green-500'}`} />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {selectedZipFile.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {(selectedZipFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            {/* Show file size error if file is too large */}
                            {fileSizeError && (
                              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-md">
                                <p className="text-sm text-red-700 dark:text-red-300 font-semibold">
                                  ⚠️ {fileSizeError}
                                </p>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setSelectedZipFile(null)
                              setFileSizeError(null) // Clear error when file is removed
                              if (zipFileInputRef.current) {
                                zipFileInputRef.current.value = ''
                              }
                            }}
                            className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
                            disabled={isCreating || isUploading}
                          >
                            Remove File
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <ArrowUpTrayIcon className="w-10 h-10 mx-auto text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              Drop your ZIP file here
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              or click to browse
                            </p>
                          </div>
                          <button
                            onClick={() => zipFileInputRef.current?.click()}
                            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                            disabled={isCreating}
                          >
                            Select ZIP File
                          </button>
                        </div>
                      )}

                      <input
                        ref={zipFileInputRef}
                        type="file"
                        accept=".zip"
                        className="hidden"
                        onChange={handleZipFileSelect}
                        disabled={isCreating}
                      />
                    </div>

                    {/* Main File Name (Optional) */}
                    {selectedZipFile && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Main .tex File (Optional)
                        </label>
                        <input
                          type="text"
                          value={mainFileName}
                          onChange={(e) => setMainFileName(e.target.value)}
                          placeholder="e.g., main.tex (auto-detected if not specified)"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={isCreating}
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Specify the main LaTeX file if auto-detection fails
                        </p>
                      </div>
                    )}

                    {/* Info */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-sm text-blue-900 dark:text-blue-200">
                        <strong>Note:</strong> The ZIP file will be uploaded immediately when you create the document. 
                        All files from the ZIP will be extracted to your project.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Upload Progress Bar - Always visible when uploading */}
          {isUploading && (
            <div 
              className="sticky bottom-0 px-6 py-4 border-t-2 border-blue-500 dark:border-blue-400 bg-white dark:bg-gray-800 shadow-lg z-20"
              style={{ position: 'sticky', bottom: 0 }}
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 dark:text-gray-300 font-medium flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                    Uploading ZIP file...
                  </span>
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">
                    {uploadProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-inner">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                    style={{ width: `${Math.max(1, Math.min(100, uploadProgress))}%` }}
                  >
                    {/* Animated shimmer effect - moving light across the bar */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                      style={{
                        animation: 'shimmer 1.5s infinite linear',
                        width: '50%',
                        transform: 'translateX(-100%)',
                      }}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Please wait while your files are being uploaded...
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 ${isUploading ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-800'} ${isUploading ? '' : 'sticky bottom-0'}`}>
            <button
              onClick={handleClose}
              disabled={isCreating || isUploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Cancel'}
            </button>
            <button
              onClick={handleCreate}
              disabled={!title.trim() || isCreating || isUploading || (selectedType === 'latex' && showZipUpload && !selectedZipFile)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Uploading... {uploadProgress}%</span>
                </>
              ) : isCreating ? (
                'Creating...'
              ) : (
                'Create Document'
              )}
            </button>
          </div>
        </motion.div>
      </div>

    </AnimatePresence>
  )
}

